import React, { useEffect, useState } from 'react';
import KIQBrief from './KIQBrief';
import KIQInsights from './KIQInsights';
import KIQActions from './KIQActions';
import KIQComposer from './KIQComposer';
import api from '../../services/api';
import { primerNombre, normalizarInsights, normalizarAcciones } from '../../utils/kiqDataAdapter';
import { text, typography } from '../design-system/tokens';

// KIQ Assistant — Fase 3 — Home. Orquesta Greeting -> Restaurant Brief ->
// Priority Insights -> Recommended Actions -> Ask KIQ (sección 6 del
// pedido).
//
// Fase 3.1 — densidad vertical reducida (sección 7 del pedido): gap entre
// secciones 22px->18px y padding del contenedor 20px->18px (junto con los
// gaps internos de cada sección, también reducidos en KIQBrief/KIQInsights/
// KIQActions/KIQComposer) — nunca con transforms/márgenes negativos, solo
// achicando la misma escala de spacing ya en uso.
//
// Fase 4 — Intelligence Engine. `insights`/`actions` ahora vienen de un
// fetch REAL a GET /api/kiq/intelligence/:anio (backend/services/kiq/*),
// nunca calculados aquí. El motor puede devolver la lista COMPLETA; esta
// capa de presentación es la única que recorta a un máximo (sección 10 del
// pedido: "el motor puede devolver todo, la presentación decide cuántos
// mostrar") — nunca al revés. Un fallo de red se degrada honestamente al
// MISMO estado vacío que ya existía en Fase 3 (arreglo vacío) — nunca
// fabrica un insight ni oculta el resto del panel (KIQBrief sigue
// cargando/mostrando su propio estado de forma completamente
// independiente, sección 13 del pedido: una fuente caída no debe tumbar
// las demás).
const MAX_INSIGHTS_MOSTRADOS = 3;
const MAX_ACCIONES_MOSTRADAS = 3;

export default function KIQHome({ usuario, resetKey }) {
  const nombre = primerNombre(usuario?.nombre);
  // Un solo cálculo de `anio` para todo KIQHome — el mismo año que pide el
  // Intelligence Engine (fetch de abajo) es el que se manda a KIQComposer
  // (Fase 5) para que "Pregúntale a KIQ" razone sobre el mismo periodo que
  // ya se ve en el Brief/Insights, nunca un año distinto o inventado.
  const anio = new Date().getFullYear();
  const [insights, setInsights] = useState([]);
  const [acciones, setAcciones] = useState([]);

  useEffect(() => {
    let cancelado = false;
    async function cargarInteligencia() {
      try {
        const { data } = await api.get(`/api/kiq/intelligence/${anio}`);
        if (cancelado) return;
        setInsights(normalizarInsights(data));
        setAcciones(normalizarAcciones(data));
      } catch {
        // Degradación honesta: sin insights/acciones reales, se muestran los
        // estados vacíos ya existentes — nunca se inventa un insight ni se
        // rompe el resto del panel (KIQBrief se carga por su cuenta).
        if (!cancelado) { setInsights([]); setAcciones([]); }
      }
    }
    cargarInteligencia();
    return () => { cancelado = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `anio` es estable durante toda la vida del componente (mismo año calendario); listado explícito solo generaría un refetch idéntico.
  }, []);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '18px', padding: '18px' }}>
      <div>
        <h1 style={{ ...typography.h3, color: text.primary, margin: 0 }}>
          {nombre ? `Hola, ${nombre}` : 'Hola'}
        </h1>
        <p style={{ ...typography.bodySmall, color: text.muted, margin: '4px 0 0' }}>
          Esto es lo que está pasando en tu restaurante.
        </p>
      </div>

      <KIQBrief />
      <KIQInsights insights={insights.slice(0, MAX_INSIGHTS_MOSTRADOS)} />
      <KIQActions actions={acciones.slice(0, MAX_ACCIONES_MOSTRADAS)} />
      {/* Shell (design_handoff_shell) — `key={resetKey}` remonta el composer
          entero cuando se pulsa "reiniciar conversación" en KIQHeader (ver
          KIQPanel.js): limpia mensajes/input sin ningún efecto/prop nuevo
          dentro de KIQComposer.js. `resetKey` es `undefined` cuando KIQHome
          se monta en /kiq (página a ancho completo, sin ese botón) — React
          trata key=undefined igual que no pasar key, sin efecto alguno. */}
      <KIQComposer anio={anio} key={resetKey} />
    </div>
  );
}
